import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import {
  Download,
  Upload,
  CheckCircle,
  AlertTriangle,
  FileText,
  ArrowLeft,
  ArrowRight,
  Info,
  FileSpreadsheet,
  RotateCcw,
  X,
  Shield,
  Loader,
  Settings,
} from 'lucide-react';
import { generateEnquiryTemplate } from './utils/enquiryTemplateGenerator';
import {
  AUTO_COLUMN_KEYS,
  EnquiryColumnDef,
  EnquiryColumnGroup,
  EnquiryImportConfig,
  buildColumnIndex,
  orderedEnabledColumns,
} from './utils/enquiryColumns';
import {
  parseEnquiryExcel,
  EnquiryRow,
  ValidationError,
  getRowErrors,
  rowHasErrors,
  ParseResult,
} from './utils/enquiryValidator';
import {
  validateEnquiryData,
  ingestEnquiries,
  getEnquiryImportConfig,
  saveEnquiryImportConfig,
  EnquiryIngestRowResult,
} from '../../_services/ingestion.service';
import ColumnConfigModal from './components/ColumnConfigModal';
import '../data-ingestion/data-ingestion.css';

type Step = 1 | 2 | 3 | 4 | 5;
type FilterMode = 'all' | 'valid' | 'errors';

const STEP_LABELS = ['Instructions', 'Upload', 'Validate & Preview', 'Submit', 'Result'];

/** How many columns the preview grid shows before it gets unreadable. */
const MAX_PREVIEW_COLUMNS = 12;

const EnquiryIngestionPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Column catalogue + tenant configuration
  const [columns, setColumns] = useState<EnquiryColumnDef[]>([]);
  const [groups, setGroups] = useState<EnquiryColumnGroup[]>([]);
  const [config, setConfig] = useState<EnquiryImportConfig | null>(null);
  const [canConfigure, setCanConfigure] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Parse / validation
  const [rows, setRows] = useState<EnquiryRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [filter, setFilter] = useState<FilterMode>('all');
  const [fileBase64, setFileBase64] = useState('');

  // Server validation
  const [validating, setValidating] = useState(false);
  const [serverValidated, setServerValidated] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [submitResult, setSubmitResult] = useState<{
    created: number;
    failed: number;
    total: number;
    results: EnquiryIngestRowResult[];
  } | null>(null);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const catalog = await getEnquiryImportConfig();
      setColumns(catalog.columns);
      setGroups(catalog.groups);
      setConfig(catalog.config);
      setCanConfigure(!!catalog.canConfigure);
    } catch (err: any) {
      setConfigError(err?.message ?? 'Could not load the import configuration');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const columnIndex = useMemo(() => buildColumnIndex(columns), [columns]);
  const enabledColumns = useMemo(
    () => (config ? orderedEnabledColumns(columns, config) : []),
    [columns, config]
  );
  const mandatorySet = useMemo(
    () => new Set(config?.mandatoryColumns ?? []),
    [config]
  );
  const labelFor = useCallback(
    (key: string) => columnIndex.get(key)?.label ?? key,
    [columnIndex]
  );

  // Mandatory columns first — they are the ones a reviewer needs to see.
  const previewColumns = useMemo(() => {
    const mandatory = enabledColumns.filter(c => mandatorySet.has(c.key));
    const rest = enabledColumns.filter(c => !mandatorySet.has(c.key));
    return [...mandatory, ...rest].slice(0, MAX_PREVIEW_COLUMNS);
  }, [enabledColumns, mandatorySet]);

  // Derived counts
  const validCount = useMemo(
    () => rows.filter((_, i) => !rowHasErrors(validationErrors, i)).length,
    [rows, validationErrors]
  );
  const errorCount = rows.length - validCount;

  const filteredRows = useMemo(() => {
    const indexed = rows.map((r, i) => ({ row: r, idx: i }));
    if (filter === 'valid') return indexed.filter(x => !rowHasErrors(validationErrors, x.idx));
    if (filter === 'errors') return indexed.filter(x => rowHasErrors(validationErrors, x.idx));
    return indexed;
  }, [rows, validationErrors, filter]);

  // --- Handlers ---

  const handleDownloadTemplate = () => {
    if (!config) return;
    generateEnquiryTemplate(columns, groups, config);
  };

  const handleSaveConfig = async (enabled: string[], mandatory: string[]) => {
    const saved = await saveEnquiryImportConfig(enabled, mandatory);
    setConfig(saved);
    // The sheet already in memory was parsed against the previous column set.
    handleReset();
  };

  const processFile = async (file: File) => {
    if (!config) return;

    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!['.xlsx', '.xls'].includes(ext)) {
      setFileError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      setFileError(null);
      setFileName(file.name);
      const buffer = await file.arrayBuffer();
      const result: ParseResult = parseEnquiryExcel(buffer, columns, config);

      if (result.rows.length === 0) {
        setFileError('No data rows found. Make sure the first sheet has column headers and data rows.');
        return;
      }

      if (result.missingColumns.length > 0) {
        setFileError(`Missing mandatory columns: ${result.missingColumns.join(', ')}`);
        return;
      }

      setRows(result.rows);
      setValidationErrors(result.errors);
      setMissingColumns(result.missingColumns);
      setUnknownHeaders(result.unknownHeaders);
      setFileBase64(result.fileBuffer);
      setServerValidated(false);
      setCurrentStep(3);
    } catch (err: any) {
      setFileError(`Failed to parse file: ${err.message}`);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await processFile(file);
  };

  const handleServerValidation = async () => {
    if (validating) return;
    setValidating(true);
    try {
      const response = await validateEnquiryData(rows);
      if (response.success && response.data) {
        const serverErrors = response.data.errors;
        if (serverErrors.length > 0) {
          const existingKeys = new Set(
            validationErrors.map(e => `${e.row}_${e.field}_${e.message}`)
          );
          const newErrors = serverErrors.filter(
            e => !existingKeys.has(`${e.row}_${e.field}_${e.message}`)
          );
          setValidationErrors(prev => [...prev, ...newErrors]);
          setServerValidated(false);
        } else {
          setServerValidated(true);
        }
      }
    } catch (err: any) {
      setFileError(`Server validation failed: ${err.message}`);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;

    if (!serverValidated) {
      await handleServerValidation();
      return;
    }

    setSubmitting(true);
    setCurrentStep(4);
    setSubmitProgress(0);

    try {
      setSubmitProgress(30);
      const response = await ingestEnquiries(rows, fileBase64, fileName || 'enquiry-upload.xlsx');
      setSubmitProgress(100);

      if (response.success && response.summary && response.results) {
        setSubmitResult({
          created: response.summary.created,
          failed: response.summary.failed,
          total: response.summary.total,
          results: response.results,
        });
      } else {
        setSubmitResult({ created: 0, failed: rows.length, total: rows.length, results: [] });
      }
      setCurrentStep(5);
    } catch (err: any) {
      setSubmitResult({ created: 0, failed: rows.length, total: rows.length, results: [] });
      setFileError(`Ingestion failed: ${err.message}`);
      setCurrentStep(5);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentStep(1); setFileName(null); setFileError(null);
    setRows([]); setValidationErrors([]); setMissingColumns([]); setUnknownHeaders([]);
    setFilter('all'); setFileBase64('');
    setValidating(false); setServerValidated(false);
    setSubmitting(false); setSubmitProgress(0); setSubmitResult(null);
  };

  const handleDownloadResults = () => {
    if (!submitResult?.results) return;
    const successResults = submitResult.results.filter(r => r.success);
    if (successResults.length === 0) return;

    const headers = ['Row', 'EnquiryId', 'FirstName', 'LastName', 'Batch', 'StudentCode', 'UserName', 'Password', 'ParentUserName'];
    const csvRows = successResults.map(r => {
      const row = rows[r.row] ?? {};
      return [
        r.row + 2, r.enquiryId ?? '', row['firstname'] ?? '', row['lastname'] ?? '',
        row['batch'] ?? '', r.studentCode ?? '', r.userName ?? '', r.password ?? '', r.parentUserName ?? '',
      ].join(',');
    });
    const csv = [headers.join(','), ...csvRows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'enquiry-ingestion-results.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // --- Step Renderers ---

  const renderInstructions = () => (
    <div className="di-instructions">
      <div className="di-info-banner">
        <Info size={20} />
        <span>
          Use this tool to bulk import student enquiry records. The template below carries the
          columns configured for this tenant
          {canConfigure ? (
            <> — use <strong>Configure Columns</strong> to change them.</>
          ) : (
            '.'
          )}
        </span>
      </div>

      {groups
        .map(group => ({ group, cols: enabledColumns.filter(c => c.group === group.key) }))
        .filter(g => g.cols.length > 0)
        .map(({ group, cols }) => (
          <div key={group.key} className="di-columns-section">
            <h3>{group.label}</h3>
            <div className="di-columns-grid">
              {cols.map(col => (
                <div key={col.key} className="di-column-item">
                  <span className="di-column-name">{col.label}</span>
                  {mandatorySet.has(col.key) && <span className="di-required-badge">Required</span>}
                  {AUTO_COLUMN_KEYS.includes(col.key) && <span className="di-auto-badge">AUTO</span>}
                </div>
              ))}
            </div>
          </div>
        ))}

      <div className="di-auto-info">
        <h3>Optional Enrollment</h3>
        <p>
          Provide a <strong>Batch</strong> (or a <strong>StudentCode</strong>) and the system also creates
          the student enrollment and login. <strong>StudentCode</strong>, <strong>UserName</strong> and{' '}
          <strong>Password</strong> accept <code>AUTO</code> — the system generates them. Leave those
          columns empty to create only the enquiry record.
        </p>
      </div>

      <div className="di-tips">
        <h3>Tips</h3>
        <ul>
          <li>Fill data in the <strong>Enquiries</strong> sheet only. The Instructions sheet is for reference.</li>
          <li>Do not change or remove the header row.</li>
          {config && config.mandatoryColumns.length > 0 ? (
            <li>
              Mandatory columns:{' '}
              <strong>{config.mandatoryColumns.map(labelFor).join(', ')}</strong>.
            </li>
          ) : (
            <li>
              No mandatory columns are configured yet
              {canConfigure ? (
                <> — set them under <strong>Configure Columns</strong>.</>
              ) : (
                ' — ask an administrator to set them up.'
              )}
            </li>
          )}
          <li>Lookup columns accept the master record's <strong>Name</strong>, its <strong>code</strong>, or its numeric <strong>Id</strong>.</li>
          <li>Gender values: <strong>M/F/O</strong> or <strong>Male/Female/Others</strong>.</li>
          <li>Date columns: <strong>YYYY-MM-DD</strong> (Excel date cells are read as-is).</li>
          <li>Headers from the Java ingestor sheet are accepted verbatim, along with the older node names.</li>
        </ul>
      </div>

      <div className="di-actions">
        <button className="di-btn-primary" onClick={handleDownloadTemplate}>
          <Download size={18} /> Download Template
        </button>
        <button className="di-btn-primary" onClick={() => setCurrentStep(2)}>
          Next: Upload File <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderUpload = () => (
    <div className="di-upload">
      <div
        className={`di-dropzone ${dragActive ? 'drag-active' : ''} ${fileName ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} style={{ display: 'none' }} />
        <div className="di-dropzone-icon">
          {fileName ? <FileSpreadsheet size={48} /> : <Upload size={48} />}
        </div>
        <div className="di-dropzone-text">
          {fileName ? (
            <><strong>{fileName}</strong><p>Click or drop to replace</p></>
          ) : (
            <><strong>Drag &amp; drop your Excel file here</strong><p>or click to browse. Accepted formats: .xlsx, .xls</p></>
          )}
        </div>
      </div>

      {fileError && (
        <div className="di-error-banner">
          <AlertTriangle size={18} /><span>{fileError}</span>
          <button className="di-error-close" onClick={() => setFileError(null)}><X size={16} /></button>
        </div>
      )}

      <div className="di-actions">
        <button className="di-btn-secondary" onClick={() => setCurrentStep(1)}>
          <ArrowLeft size={18} /> Back
        </button>
        <button className="di-btn-secondary" onClick={handleDownloadTemplate}>
          <Download size={18} /> Download Template
        </button>
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="di-preview">
      <div className="di-summary-bar">
        <div className="di-summary-item"><FileText size={18} /><span><strong>{rows.length}</strong> Total Rows</span></div>
        <div className="di-summary-item di-summary-valid"><CheckCircle size={18} /><span><strong>{validCount}</strong> Valid</span></div>
        <div className="di-summary-item di-summary-error"><AlertTriangle size={18} /><span><strong>{errorCount}</strong> Errors</span></div>
      </div>

      {missingColumns.length > 0 && (
        <div className="di-error-banner">
          <AlertTriangle size={18} /><span>Missing mandatory columns: {missingColumns.join(', ')}</span>
        </div>
      )}

      {unknownHeaders.length > 0 && (
        <div className="di-info-banner">
          <Info size={18} />
          <span>
            Ignored (not enabled for this tenant): {unknownHeaders.join(', ')}
          </span>
        </div>
      )}

      <div className="di-filter-tabs">
        <button className={`di-filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All ({rows.length})</button>
        <button className={`di-filter-tab ${filter === 'valid' ? 'active' : ''}`} onClick={() => setFilter('valid')}>Valid ({validCount})</button>
        <button className={`di-filter-tab ${filter === 'errors' ? 'active' : ''}`} onClick={() => setFilter('errors')}>Errors ({errorCount})</button>
      </div>

      {enabledColumns.length > previewColumns.length && (
        <p className="di-progress-text" style={{ textAlign: 'left', margin: '0 0 8px' }}>
          Showing {previewColumns.length} of {enabledColumns.length} columns. All configured columns are
          still imported.
        </p>
      )}

      <div className="di-table-wrapper">
        <table className="di-preview-table">
          <thead>
            <tr>
              <th className="di-th-row">#</th>
              <th className="di-th-status">Status</th>
              {previewColumns.map(col => <th key={col.key}>{col.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(({ row, idx }) => {
              const hasError = rowHasErrors(validationErrors, idx);
              const rowErrors = getRowErrors(validationErrors, idx);
              const errorFields = new Set(rowErrors.map(e => e.field));
              return (
                <React.Fragment key={idx}>
                  <tr className={hasError ? 'di-row-error' : 'di-row-valid'}>
                    <td className="di-td-row">{idx + 2}</td>
                    <td className="di-td-status">
                      {hasError ? (
                        <span className="di-status-badge di-status-error">
                          <AlertTriangle size={14} /> {rowErrors.length} error{rowErrors.length > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="di-status-badge di-status-valid"><CheckCircle size={14} /> Valid</span>
                      )}
                    </td>
                    {previewColumns.map(col => (
                      <td key={col.key} className={errorFields.has(col.key) ? 'di-cell-error' : ''}>
                        {row[col.key] || <span className="di-empty-cell">-</span>}
                      </td>
                    ))}
                  </tr>
                  {hasError && (
                    <tr className="di-error-detail-row">
                      <td colSpan={previewColumns.length + 2}>
                        <div className="di-error-details">
                          {rowErrors.map((err, ei) => (
                            <span key={ei} className="di-error-tag">
                              <strong>{labelFor(err.field)}:</strong> {err.message}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {fileError && (
        <div className="di-error-banner" style={{ marginTop: 16 }}>
          <AlertTriangle size={18} /><span>{fileError}</span>
          <button className="di-error-close" onClick={() => setFileError(null)}><X size={16} /></button>
        </div>
      )}

      <div className="di-actions">
        <button className="di-btn-secondary" onClick={() => { setCurrentStep(2); setRows([]); setValidationErrors([]); setFilter('all'); setServerValidated(false); }}>
          <ArrowLeft size={18} /> Back to Upload
        </button>
        {!serverValidated ? (
          <button className="di-btn-primary di-btn-validate" onClick={handleSubmit} disabled={validCount === 0 || validating || submitting}>
            {validating ? (<><Loader size={18} className="di-spin" /> Validating...</>) : (<><Shield size={18} /> Validate with Server</>)}
          </button>
        ) : (
          <button className="di-btn-primary" onClick={handleSubmit} disabled={validCount === 0 || submitting}>
            {submitting ? (<><Loader size={18} className="di-spin" /> Submitting...</>) : (<>Submit {validCount} Row{validCount !== 1 ? 's' : ''} <ArrowRight size={18} /></>)}
          </button>
        )}
      </div>
    </div>
  );

  const renderSubmitting = () => (
    <div className="di-submitting">
      <div className="di-progress-container">
        <div className="di-progress-icon"><Upload size={40} className="di-spin" /></div>
        <h3>Importing {validCount} enquiries...</h3>
        <div className="di-progress-bar"><div className="di-progress-fill" style={{ width: `${submitProgress}%` }} /></div>
        <p className="di-progress-text">{submitProgress}% complete</p>
      </div>
    </div>
  );

  const renderResult = () => (
    <div className="di-result">
      {submitResult && submitResult.created > 0 ? (
        <div className="di-result-success">
          <div className="di-result-icon di-result-icon-success"><CheckCircle size={48} /></div>
          <h2>Import {submitResult.failed === 0 ? 'Successful' : 'Partially Successful'}</h2>
          <p>
            {submitResult.created} of {submitResult.total} enquir{submitResult.total !== 1 ? 'ies' : 'y'} created successfully.
            {submitResult.failed > 0 && ` ${submitResult.failed} failed.`}
          </p>
          <div className="di-result-stats">
            <div className="di-stat-card"><div className="di-stat-value">{submitResult.created}</div><div className="di-stat-label">Created</div></div>
            <div className="di-stat-card"><div className="di-stat-value">{submitResult.failed}</div><div className="di-stat-label">Failed</div></div>
          </div>
        </div>
      ) : (
        <div className="di-result-error">
          <div className="di-result-icon di-result-icon-error"><AlertTriangle size={48} /></div>
          <h2>Import Failed</h2>
          <p>{fileError || 'Something went wrong during the import. Please try again.'}</p>
        </div>
      )}

      {submitResult?.results && submitResult.results.length > 0 && (
        <div className="di-results-table-section">
          <h3>Ingestion Results</h3>
          <div className="di-table-wrapper">
            <table className="di-preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Status</th>
                  <th>FirstName</th>
                  <th>LastName</th>
                  <th>EnquiryId</th>
                  <th>Batch</th>
                  <th>Student Code</th>
                  <th>User Name</th>
                  <th>Password</th>
                  <th>Parent User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {submitResult.results.map((r) => (
                  <tr key={r.row} className={r.success ? 'di-row-valid' : 'di-row-error'}>
                    <td>{r.row + 2}</td>
                    <td>
                      {r.success
                        ? <span className="di-status-badge di-status-valid"><CheckCircle size={14} /> {r.enrolled ? 'Enrolled' : 'Created'}</span>
                        : <span className="di-status-badge di-status-error"><AlertTriangle size={14} /> Failed</span>}
                    </td>
                    <td>{rows[r.row]?.['firstname'] || '-'}</td>
                    <td>{rows[r.row]?.['lastname'] || '-'}</td>
                    <td>{r.enquiryId || '-'}</td>
                    <td>{rows[r.row]?.['batch'] || '-'}</td>
                    <td>{r.studentCode || '-'}</td>
                    <td>{r.userName || '-'}</td>
                    <td>{r.password || '-'}</td>
                    <td>{r.parentUserName || '-'}</td>
                    <td className="di-td-details" title={r.error || (r.enrolled ? `Enrollment ID: ${r.enrollmentId}` : `Enquiry ID: ${r.enquiryId}`)}>
                      {r.error || (r.enrolled ? `Enrollment ID: ${r.enrollmentId}` : `Enquiry ID: ${r.enquiryId}`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="di-actions di-actions-center">
        {submitResult?.results && submitResult.results.some(r => r.success) && (
          <button className="di-btn-secondary" onClick={handleDownloadResults}>
            <Download size={18} /> Download Results
          </button>
        )}
        <button className="di-btn-primary" onClick={handleReset}>
          <RotateCcw size={18} /> Import Another File
        </button>
      </div>
    </div>
  );

  // --- Main Render ---

  return (
    <div className="data-ingestion">
      <div className="di-header">
        <h1>Enquiry Data Ingestion</h1>
        <p>Bulk import student enquiry records from Excel</p>
        {/* Column set is tenant-wide, so only the root login may change it. */}
        {canConfigure && (
          <div className="di-actions" style={{ marginTop: 12 }}>
            <button
              className="di-btn-secondary"
              onClick={() => setShowConfigModal(true)}
              disabled={configLoading || !config}
            >
              <Settings size={18} /> Configure Columns
              {config ? ` (${config.enabledColumns.length})` : ''}
            </button>
          </div>
        )}
      </div>

      <div className="di-stepper">
        {STEP_LABELS.map((label, idx) => {
          const stepNum = (idx + 1) as Step;
          const isActive = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;
          return (
            <React.Fragment key={stepNum}>
              <div className={`di-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                <div className="di-step-circle">{isCompleted ? <CheckCircle size={16} /> : stepNum}</div>
                <span className="di-step-label">{label}</span>
              </div>
              {idx < STEP_LABELS.length - 1 && (
                <div className={`di-step-connector ${isCompleted ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="di-content-card">
        {configLoading && (
          <div className="di-submitting">
            <div className="di-progress-container">
              <div className="di-progress-icon"><Loader size={40} className="di-spin" /></div>
              <h3>Loading column configuration…</h3>
            </div>
          </div>
        )}

        {!configLoading && configError && (
          <div className="di-error-banner">
            <AlertTriangle size={18} /><span>{configError}</span>
            <button className="di-btn-secondary" onClick={loadConfig} style={{ marginLeft: 'auto' }}>
              Retry
            </button>
          </div>
        )}

        {!configLoading && !configError && config && (
          <>
            {currentStep === 1 && renderInstructions()}
            {currentStep === 2 && renderUpload()}
            {currentStep === 3 && renderPreview()}
            {currentStep === 4 && renderSubmitting()}
            {currentStep === 5 && renderResult()}
          </>
        )}
      </div>

      {config && canConfigure && (
        <ColumnConfigModal
          show={showConfigModal}
          columns={columns}
          groups={groups}
          config={config}
          onClose={() => setShowConfigModal(false)}
          onSave={handleSaveConfig}
        />
      )}
    </div>
  );
};

export default EnquiryIngestionPage;
